import { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';

/**
 * Custom hook for Supabase Realtime subscriptions
 * @param {string} table - Table name to subscribe to
 * @param {function} onDataChange - Callback when data changes
 * @param {object} options - Filter options
 */
export const useRealtime = (table, onDataChange, options = {}) => {
    useEffect(() => {
        console.log(`Setting up realtime for ${table}...`);
        
        const channel = supabase
            .channel(`${table}-changes`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: table,
                    ...options
                },
                (payload) => {
                    console.log(`Realtime update for ${table}:`, payload);
                    onDataChange(payload);
                }
            )
            .subscribe((status) => {
                console.log(`${table} subscription status:`, status);
            });

        return () => {
            console.log(`Unsubscribing from ${table}`);
            supabase.removeChannel(channel);
        };
    }, [table]);
};

/**
 * Hook for real-time contact messages (admin)
 */
export const useRealtimeContacts = (onNewMessage) => {
    useRealtime('contact_messages', (payload) => {
        if (payload.eventType === 'INSERT') {
            onNewMessage(payload.new);
        }
    });
};

/**
 * Hook for real-time sessions (admin)
 */
export const useRealtimeSessions = (onNewSession) => {
    useRealtime('sessions', (payload) => {
        if (payload.eventType === 'INSERT') {
            onNewSession(payload.new);
        }
    });
};

/**
 * Hook for real-time calendar requests (admin)
 */
export const useRealtimeCalendar = (onNewRequest) => {
    useRealtime('calendar_requests', (payload) => {
        if (payload.eventType === 'INSERT') {
            onNewRequest(payload.new);
        }
    });
};

/**
 * Hook for real-time community questions (admin)
 */
export const useRealtimeQuestions = (onNewQuestion) => {
    useRealtime('community_questions', (payload) => {
        if (payload.eventType === 'INSERT') {
            onNewQuestion(payload.new);
        }
    });
};

/**
 * Hook for real-time jobs (user)
 */
export const useRealtimeJobs = (onJobChange) => {
    useRealtime('jobs', (payload) => {
        onJobChange(payload);
    });
};

/**
 * Hook for real-time events (user)
 */
export const useRealtimeEvents = (onEventChange) => {
    useRealtime('events', (payload) => {
        onEventChange(payload);
    });
};

/**
 * Hook for real-time notifications
 */
export const useRealtimeNotifications = (userId, onNotification) => {
    useRealtime('notifications', (payload) => {
        if (payload.new?.user_id === userId) {
            onNotification(payload);
        }
    }, { filter: `user_id=eq.${userId}` });
};

export default useRealtime;
